import { useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowLeft, Loader2, MapPin } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { PlaceSearchInput } from "@/components/map/PlaceSearchInput";
import { PolygonDrawer } from "@/components/map/PolygonDrawer";
import type { PolygonData } from "@/components/map/PolygonDrawer";
import { DEFAULT_CLASSIFICATION_TYPES } from "@/types/domain";

const SPORT_TYPE_LABELS: Record<string, string> = {
  soccer_pitch: "Soccer Pitch",
  tennis_court: "Tennis Court",
  padel_court: "Padel Court",
  basketball_court: "Basketball Court",
  volleyball_court: "Volleyball Court",
  running_track: "Running Track",
  baseball_diamond: "Baseball Diamond",
  rugby_pitch: "Rugby Pitch",
  field_hockey_pitch: "Field Hockey Pitch",
  cricket_pitch: "Cricket Pitch",
  skateboard_park: "Skateboard Park",
  multi_sport_court: "Multi-Sport Court",
  beach_volleyball_court: "Beach Volleyball Court",
  petanque_court: "Petanque Court",
  street_workout_area: "Street Workout Area",
  pickleball_court: "Pickleball Court",
  ping_pong_table: "Ping Pong Table",
};

interface FormValues {
  name: string;
  notes: string;
  includeInstitutional: boolean;
  includePrivate: boolean;
  includeNoSportField: boolean;
}

interface ScanRequestFormProps {
  onClose: () => void;
}

export function ScanRequestForm({ onClose }: ScanRequestFormProps) {
  const [polygon, setPolygon] = useState<PolygonData | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedSportTypes, setSelectedSportTypes] = useState<string[]>(
    [...DEFAULT_CLASSIFICATION_TYPES].filter((t) => t !== "skateboard_park"),
  );

  const cityBoundaryQuery = trpc.scanRequests.searchCity.useQuery(
    { query: selectedCity! },
    { enabled: !!selectedCity },
  );

  const utils = trpc.useUtils();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      name: "",
      notes: "",
      includeInstitutional: false,
      includePrivate: false,
      includeNoSportField: false,
    },
  });

  const createMutation = trpc.scanRequests.create.useMutation({
    onSuccess: () => {
      utils.scanRequests.list.invalidate();
      toast.success("Scan request created");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleSportType = (type: string) => {
    setSelectedSportTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const onSubmit = (data: FormValues) => {
    if (!polygon) {
      toast.error("Please draw a polygon on the map");
      return;
    }

    createMutation.mutate({
      name: data.name,
      polygon,
      requestedVariables: {
        sportTypes: selectedSportTypes,
        excludeInstitutions: !data.includeInstitutional,
        excludePrivate: !data.includePrivate,
        excludeNoSportField: !data.includeNoSportField,
      },
      notes: data.notes || null,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">New Scan Request</h1>
          <p className="text-muted-foreground text-sm">
            Draw a polygon to define the scan area
          </p>
        </div>
      </div>

      {/* Name */}
      <Card>
        <CardHeader>
          <CardTitle>Name</CardTitle>
          <CardDescription>
            Give this scan request a descriptive name.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            {...register("name", { required: true, maxLength: 255 })}
            placeholder="e.g. Amsterdam Sport Fields"
          />
          {errors.name && (
            <p className="text-xs text-destructive mt-1">
              Name is required (max 255 characters)
            </p>
          )}
        </CardContent>
      </Card>

      {/* Polygon Map */}
      <Card>
        <CardHeader>
          <CardTitle>Scan Area</CardTitle>
          <CardDescription>
            Click on the map to draw a polygon. Click the first vertex or double-click to close.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative w-full h-[400px] rounded-lg overflow-hidden border">
            <GoogleMapWrapper
              defaultCenter={{ lat: 52.1326, lng: 5.2913 }}
              defaultZoom={7}
              mapTypeId="hybrid"
            >
              <PlaceSearchInput onCitySelected={setSelectedCity} />
              <PolygonDrawer value={polygon} onChange={setPolygon} />
            </GoogleMapWrapper>
          </div>
          {!polygon && (
            <p className="text-xs text-muted-foreground mt-2">
              Required. Draw a polygon on the map above.
            </p>
          )}
          {cityBoundaryQuery.data?.polygon && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">
                Boundary found for{" "}
                <span className="font-medium text-foreground">
                  {cityBoundaryQuery.data.name.split(",")[0]}
                </span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="ml-auto shrink-0 h-7 text-xs"
                onClick={() => {
                  setPolygon({
                    coordinates: cityBoundaryQuery.data!.polygon.coordinates,
                  });
                }}
              >
                Use city boundary
              </Button>
            </div>
          )}
          {cityBoundaryQuery.isFetching && (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching for city boundary...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sport Types */}
      <Card>
        <CardHeader>
          <CardTitle>Sport Types</CardTitle>
          <CardDescription>
            Select the sport types to scan for. Leave empty to scan for all types.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {DEFAULT_CLASSIFICATION_TYPES.filter((t) => t !== "skateboard_park").map((type) => (
              <div key={type} className="flex items-center gap-2">
                <Checkbox
                  id={`sport-${type}`}
                  checked={selectedSportTypes.includes(type)}
                  onCheckedChange={() => toggleSportType(type)}
                />
                <Label htmlFor={`sport-${type}`} className="cursor-pointer text-sm">
                  {SPORT_TYPE_LABELS[type] ?? type}
                </Label>
              </div>
            ))}
          </div>
          {selectedSportTypes.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {selectedSportTypes.length} type{selectedSportTypes.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </CardContent>
      </Card>

      {/* Location Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Location Filters</CardTitle>
          <CardDescription>
            Include these location types in scan results.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="includeInstitutional"
              checked={watch("includeInstitutional")}
              onCheckedChange={(checked) => setValue("includeInstitutional", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="includeInstitutional" className="cursor-pointer">
                Include institutional locations
              </Label>
              <p className="text-xs text-muted-foreground">
                Schools, universities, colleges, and preschools
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="includePrivate"
              checked={watch("includePrivate")}
              onCheckedChange={(checked) => setValue("includePrivate", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="includePrivate" className="cursor-pointer">
                Include private facilities
              </Label>
              <p className="text-xs text-muted-foreground">
                Private gyms, clubs, and membership-only venues
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="includeNoSportField"
              checked={watch("includeNoSportField")}
              onCheckedChange={(checked) => setValue("includeNoSportField", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="includeNoSportField" className="cursor-pointer">
                Include locations without sport fields
              </Label>
              <p className="text-xs text-muted-foreground">
                Locations where satellite imagery shows no sport field
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
          <CardDescription>
            Optional notes about this scan request.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            {...register("notes", { maxLength: 2000 })}
            placeholder="Any additional context or instructions..."
            rows={4}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Max 2000 characters
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit Request
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
